import React, { useContext, createContext, useState, useCallback } from 'react';
import { useAuth } from 'hooks/useAuth/useAuth';
import {
  postCreateVehicle,
  getVehiclesSuitability,
  postAssignVehicles,
} from 'utils/network/http';
import { VEHICLE_TYPES, TRAILER_SPECS } from 'utils/constants/vehicles';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import { useAssignment } from 'hooks/useAssignment/useAssignment';

export const VehiclesContext = createContext();

export const ProvideVehicles = ({ children }) => {
  const vehiclesData = useProvideVehicles();
  return (
    <VehiclesContext.Provider value={vehiclesData}>
      {children}
    </VehiclesContext.Provider>
  );
};

export const useVehicles = () => {
  return useContext(VehiclesContext);
};

const checkIfIsOnlyTrailerJob = (vehicles) =>
  vehicles
    .filter((vehicle) => vehicle.is_suitable === true)
    .every(
      (vehicle) =>
        vehicle.vehicle_type === VEHICLE_TYPES.tractor_unit ||
        vehicle.vehicle_type === VEHICLE_TYPES.trailer
    );

const useProvideVehicles = () => {
  const [showFastVehicleCreation, setShowFastVehicleCreation] = useState(false);
  const [candidatePlateNumber, setCandidatePlateNumber] = useState('');
  const [candidateType, setCandidateType] = useState(null);
  const [candidateExtras, setCandidateExtras] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingSuitableVehicles, setLoadingSuitableVehicles] = useState(false);
  const [error, setError] = useState(false);
  const [suitableVehicles, setSuitableVehicles] = useState(null);
  const [isTrailerJob, setIsTrailerJob] = useState(false);
  const { profile, setProfile } = useAuth();
  const { findVehicle } = useAssignment();

  const handleShowFastVehicleCreation = useCallback((value) => {
    setShowFastVehicleCreation(value);
  }, []);

  const handleAddVehicleExtras = useCallback(
    (extra) => {
      setCandidateExtras({ ...candidateExtras, ...extra });
    },
    [candidateExtras]
  );

  const assignVehicles = useCallback(
    async (selectedVehicles, journeyId, refreshJourney) => {
      try {
        setLoading(true);
        setError(false);
        sendEventToSegment(TRACKING_EVENTS.VEHICLE_ASSIGN, {
          vehicles: selectedVehicles,
          journeyId,
        });
        await postAssignVehicles(journeyId, selectedVehicles);
        sendEventToSegment(TRACKING_EVENTS.FAST_VEHICLE_CREATED_AND_ASSIGNED, {
          journeyId,
          carrierId: profile.carrier.id,
        });
      } catch (err) {
        console.error('Error postAssignVehicles', err);
        setError(true);
      } finally {
        await refreshJourney();
        setLoading(false);
      }
    },
    [profile]
  );

  const overwriteOrSaveVehicle = useCallback(
    (currentAssignedVehicles, newVehicleType) => {
      const vehicleType = findVehicle(currentAssignedVehicles[0])?.vehicle_type;
      return currentAssignedVehicles.length === 1 &&
        vehicleType !== newVehicleType
        ? currentAssignedVehicles
        : [];
    },
    [findVehicle]
  );

  const checkIfVehicleAlreadyExists = useCallback(
    (plate_number) => {
      if (!profile.vehicles) return false;
      return profile.vehicles.some(
        (vehicle) => vehicle.plate_number === plate_number
      );
    },
    [profile.vehicles]
  );

  const createAndAssignVehicle = useCallback(
    async (journeyId, refreshJourney, currentAssignedVehicles) => {
      try {
        setLoading(true);
        setError(false);
        const { data } = await postCreateVehicle({
          carrier: profile.carrier.url,
          plate_number: candidatePlateNumber,
          status: 'active',
          vehicle_type: candidateType,
          ...(candidateType === VEHICLE_TYPES.trailer && TRAILER_SPECS),
          ...candidateExtras,
        });
        setProfile({ ...profile, vehicles: [...profile.vehicles, data] });
        await assignVehicles(
          [
            data.id,
            ...overwriteOrSaveVehicle(
              currentAssignedVehicles,
              data.vehicle_type
            ),
          ],
          journeyId,
          refreshJourney
        );
        setShowFastVehicleCreation(false);
        setCandidatePlateNumber('');
        setCandidateType(null);
        setCandidateExtras({});
      } catch (e) {
        setError(true);
        setLoading(false);
        console.error('Error createAndAssignVehicle', e);
      }
    },
    [
      candidatePlateNumber,
      profile,
      assignVehicles,
      setProfile,
      candidateType,
      candidateExtras,
      overwriteOrSaveVehicle,
    ]
  );

  const fetchSuitableVehicles = useCallback(async (journeyId) => {
    try {
      setLoadingSuitableVehicles(true);
      setError(false);
      const response = await getVehiclesSuitability(journeyId);
      const data = response.data;
      setSuitableVehicles(data);
      setIsTrailerJob(checkIfIsOnlyTrailerJob(data));
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoadingSuitableVehicles(false);
    }
  }, []);

  return {
    showFastVehicleCreation,
    handleShowFastVehicleCreation,
    candidatePlateNumber,
    setCandidatePlateNumber,
    candidateType,
    setCandidateType,
    createAndAssignVehicle,
    loading,
    error,
    suitableVehicles,
    fetchSuitableVehicles,
    loadingSuitableVehicles,
    isTrailerJob,
    handleAddVehicleExtras,
    candidateExtras,
    checkIfVehicleAlreadyExists,
  };
};
